import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm, useFieldArray } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { usePaymentInvoiceReturnRefundDetailsAPI } from "@/app/hooks/api_hooks/payment/usePaymentInvoiceReturnRefundDetailsAPI";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import useFormValidations from "@/app/hooks/useFormValidations";
import { BiRupee } from "react-icons/bi";
import clsx from "clsx";
import { IRefundInvoiceProps } from "@/api_framework/api_modals/payment";
import { usePaymentUserRefundAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserRefundAPI";
import { IInvoiceProps, useCartInvoiceListAPI } from "@/app/hooks/api_hooks/Cart/useCartInvoiceListAPI";
import { ICartState, RESET_INVOICE_CART_SLICE } from "@/app_redux/reducers/slices/auth/cart_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useDispatch } from "react-redux";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";

type Props = {
  refundState: boolean;
  setRefundState: (value: boolean) => void;
  setInitialState?: (value: boolean) => void;
  invoiceId: string;
  invoiceType: string;
  source: string;
};

const InvoiceRefundModalComponent: React.FC<Props> = ({
  refundState,
  setRefundState,
  invoiceId,
  invoiceType,
  source,
  setInitialState,
}) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const dispatch = useDispatch();
  const { invoiceListFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;

  const { execute: fetchInvoiceReturnRefund, invoiceDetailsData } = usePaymentInvoiceReturnRefundDetailsAPI();
  const { execute: refundInvoice } = usePaymentUserRefundAPI();
  const { execute: fetchInvoiceList } = useCartInvoiceListAPI();

  const [loader, setLoader] = useState<boolean>(false);
  //Default State
  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  //FromHook
  const { control, handleSubmit, formState } = formHook;

  const { fields: productInfo, append: updateProducts } = useFieldArray({
    control,
    name: "products",
  });
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();

  const onSubmit = (data) => {
    setLoader(true);

    const formdata = data?.products;

    const updatedPetWiseItems = invoiceDetailsData?.pet_wise_items?.map((pet) => {
      return {
        ...pet,
        items: pet.items
          .map((item) => {
            const matchingData = formdata?.find((dataItem) => dataItem.unique_row_id === item.unique_row_id);
            if (matchingData && parseFloat(matchingData.refund_request_amount) !== 0) {
              return {
                ...item,
                refund_request_amount: parseFloat(matchingData.refund_request_amount),
                reason: matchingData.reason,
              };
            }
            return null;
          })
          .filter((item) => item !== null),
      };
    });

    const constructedBody: IRefundInvoiceProps = {
      invoice_id: invoiceId,
      refund_items: updatedPetWiseItems as any,
    };
    refundInvoice(constructedBody).then(({ status }) => {
      if (status) {
        if (source === "payment_page") {
          setLoader(false);
          dispatch(RESET_INVOICE_CART_SLICE());
          const constructedBody: IInvoiceProps = {
            user_id: customerId,

            start_date: null,
            end_date: null,
            statuses: [],
          };
          window.location.reload();
          // fetchInvoiceList(constructedBody, "payment_page").then(({ stat }) => {
          //   if (stat) {
          //     setInitialState(false);
          //     setRefundState(false);
          //   } else {
          //     setInitialState(false);
          //     setRefundState(false);
          //   }
          // });
        } else {
          fetchUserWalletDetails(customerId);
          setLoader(false);
          const constructedBodyFilter: IInvoiceProps = {
            user_id: invoiceListFilter?.user_id,
            start_date: invoiceListFilter?.start_date,
            end_date: invoiceListFilter?.end_date,

            statuses: invoiceListFilter.statuses,
          };
          fetchInvoiceList(constructedBodyFilter, source);
          setRefundState(false);
        }
      } else {
        setLoader(false);
      }
    });
  };

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceReturnRefund(invoiceId, "refund");
    }
  }, [invoiceId]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (invoiceDetailsData?.pet_wise_items?.length > 0 && !hasInitialized.current) {
      const allItems = invoiceDetailsData?.pet_wise_items?.flatMap((petWiseItem) => petWiseItem.items);
      updateProducts(allItems.map((item) => ({ ...item, refund_request_amount: 0, reason: null })));
      hasInitialized.current = true;
    }
  }, [invoiceDetailsData, hasInitialized]);

  const totalAmountToPaid = () => {
    const totals = formHook.watch("products")?.reduce(
      (acc, item) => {
        acc.totalToBePaid += parseFloat(item?.refund_request_amount) ?? 0;
        return acc;
      },
      {
        totalToBePaid: 0,
      },
    );
    return totals;
  };

  return (
    <div>
      <Transition.Root show={refundState} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setRefundState(false);
            formHook.reset();
            hasInitialized.current = false;
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-7xl p-6"
              >
                <div className="flex items-center justify-between border-b pb-1">
                  <div>
                    <h2 className="text-xs font-semibold ">{invoiceType} Request</h2>
                    <p className="text-xs text-[#808DA0] pt-1">{invoiceId}</p>
                  </div>
                  <p>
                    <XMarkIcon
                      className="h-4 w-4 cursor-pointer font-bold"
                      onClick={() => {
                        hasInitialized.current = false;
                        formHook.reset();
                        setRefundState(false);
                      }}
                    />
                  </p>
                </div>
                <div className="my-3">
                  <p className="text-sm font-medium my-2">Select items for {invoiceType}</p>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F4F8FF]">
                        <th className="p-3 text-xs font-medium">Item</th>
                        <th className="p-3 text-xs font-medium">Lot Details</th>
                        <th className="p-3 text-xs font-medium">Ordered Qty</th>
                        <th className="p-3 text-xs font-medium">Unit Price</th>
                        <th className="p-3 text-xs font-medium">Net Price</th>
                        <th className="p-3 text-xs font-medium">Refund Amount</th>
                        <th className="p-3 text-xs font-medium">Reason for Refund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productInfo?.map((item: any, index: number) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-3 py-4 text-xs">
                            <div>{formHook.watch(`products.${index}.name`)}</div>
                            <div className="text-green-400">{formHook.watch(`products.${index}.pet_name`)}</div>
                          </td>
                          <td className="p-3 py-4 text-xs ">
                            {Object.keys(formHook.watch(`products.${index}.lot_details`))?.length === 0 ? (
                              <span>NA</span>
                            ) : (
                              <>
                                {" "}
                                <span>
                                  {formHook.watch(`products.${index}.lot_details.vetbuddy_lot_number`)},
                                  {formHook.watch(`products.${index}.lot_details.vetbuddy_manufacturer_name`)}
                                </span>
                                <span className="flex items-center">
                                  {formHook.watch(`products.${index}.lot_details.expiry_date`)} ,{" "}
                                  <span className="flex items-center">
                                    <BiRupee className="w-4 h-4" />{" "}
                                    {formHook.watch(`products.${index}.lot_details.vetbuddy_mrp`)}
                                  </span>
                                </span>
                              </>
                            )}
                          </td>
                          <td className="p-3 py-4 text-xs ">{formHook.watch(`products.${index}.qty`)}</td>
                          <td className="p-3 py-4 text-xs ">{formHook.watch(`products.${index}.unit_price`)}</td>
                          <td className="p-3 py-4 text-xs ">
                            {formHook.watch(`products.${index}.net_price`)}
                            {formHook.watch(`products.${index}.note`) && (
                              <div className="text-red-500 text-[10px] mt-1">{formHook.watch(`products.${index}.note`)}</div>
                            )}
                          </td>
                          <td className="p-3 py-4 text-xs   whitespace-nowrap ">
                            {formHook.watch(`products.${index}.is_refund_eligible`) !== false ? (
                              <div className="-mt-1">
                                {formHook.watch(`products.${index}.eligible_refund_amount`) > 0 ? (
                                  <>
                                    {" "}
                                    <NumberInput
                                      className="text-xs w-full"
                                      placeholder="Enter Amount"
                                      disabled={
                                        formHook.watch(`products.${index}.eligible_refund_amount`) === 0 ? true : false
                                      }
                                      register={formHook.register(`products.${index}.refund_request_amount`, {
                                        required: true,
                                        ...forOnlyNumbersWithDecimal.validations,
                                        validate: (value) => {
                                          if (typeof value !== "string") {
                                            return "Invalid input";
                                          }

                                          // Trim leading zeros
                                          const trimmedValue = value.replace(/^0+/, "") || "0"; // Default to '0' if empty
                                          formHook.setValue(`products.${index}.refund_request_amount`, trimmedValue);

                                          // Validate the amount is less than or equal to `dt`
                                          const dt = formHook.getValues(`products.${index}.eligible_refund_amount`);
                                          if (parseFloat(trimmedValue) > dt) {
                                            return `Amount cannot exceed ${dt}`;
                                          }

                                          return true;
                                        },
                                      })}
                                      fieldError={formState.errors.invoices?.[index]?.refund_request_amount}
                                      errorMessages={[
                                        { message: "", type: "required" },
                                        forOnlyNumbersWithDecimal.errors,
                                      ]}
                                    />
                                    <p className="text-red-400 text-[9px] absolute  ">
                                      {parseFloat(formHook.watch(`products.${index}.refund_request_amount`)) >
                                        formHook.getValues(`products.${index}.eligible_refund_amount`) &&
                                        "Amount cannot be more than net price "}
                                    </p>
                                  </>
                                ) : (
                                  <p>NA</p>
                                )}
                                {formHook.getValues(`products.${index}.already_refund_amount`) > 0 && (
                                  <p className="text-xs text-[#CB9A07] mt-3">
                                    Already Refunded:
                                    {formHook.getValues(`products.${index}.already_refund_amount`)}{" "}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span>Non-Refundable</span>
                            )}
                          </td>

                          <td className="p-3 py-4 text-xs ">
                            {formHook.watch(`products.${index}.is_refund_eligible`) !== false &&
                            formHook.watch(`products.${index}.eligible_refund_amount`) > 0 ? (
                              <>
                                {" "}
                                <textarea
                                  style={{
                                    overflowY: "scroll", // Always show vertical scrollbar
                                    scrollbarWidth: "thin", // Works for Firefox
                                  }}
                                  className={clsx(
                                    `rounded-md w-full h-full  border-gray-300 text-xs  ${formHook?.watch(`products?.${index}.reason`) === null && "outline-red-300 border border-red-600 "}`,
                                  )}
                                  placeholder="Please enter reason"
                                  {...formHook.register(`products.${index}.reason`, {
                                    required:
                                      formHook.watch(`products.${index}.refund_request_amount`) > 0 ? true : false,
                                    minLength: 1,
                                  })}
                                />
                                {formHook?.formState?.errors?.products?.[index]?.reason && (
                                  <p className="text-red-500 absolute text-[10px] -mt-1">Required</p>
                                )}
                              </>
                            ) : (
                              <span>NA</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-[#F2F2F2]">
                  <p className="text-sm font-medium p-3 shadow-sm">
                    Total {invoiceType} Amount: {productInfo?.length > 0 && totalAmountToPaid()?.totalToBePaid}
                  </p>
                </div>
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRefundState(false);
                      formHook.reset();
                      hasInitialized.current = false;
                    }}
                    className="rounded-md bg-[#72767f1a] px-4 py-2 text-sm text-[#000000] font-medium "
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`rounded-md ${totalAmountToPaid()?.totalToBePaid === 0 ? "bg-gray-400" : "bg-[#193A6A]"} font-bold px-4 py-2 text-sm text-white `}
                    disabled={totalAmountToPaid()?.totalToBePaid === 0}
                  >
                    {loader ? (
                      <div className="">
                        {" "}
                        <CircleSpinnerLoader h="20" w="20" />
                      </div>
                    ) : (
                      <span> {invoiceType} Invoice</span>
                    )}
                  </button>
                </div>
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default InvoiceRefundModalComponent;
